import React, { useEffect, useRef, useState } from "react";
import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined, LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import { updateSchemaModelRequest } from "store/schemaModels/actions";
import { buildToolError } from "./errorEnvelope";
import { fetchSchemaById, type FetchedSchema, type FetchedField } from "./schemaApi";
import {
    buildDeleteFieldPayload,
    buildFieldPayload,
    buildSchemaUpdateRequest,
    buildUpdateFieldPayload,
    type AssistantFieldInput,
    type AssistantUpdateFieldInput,
} from "./payload";
import { verifyUpdatedSchema } from "./verify";
import type { ToolPartProps } from "./types";

interface UpdateSchemaInput {
    schemaId: string;
    schemaName: string;
    name?: string;
    description?: string;
    addFields?: AssistantFieldInput[];
    updateFields?: AssistantUpdateFieldInput[];
    deleteFields?: Array<{ fieldName: string }>;
}

interface UpdateSchemaOutput {
    success: boolean;
    cancelled?: boolean;
    error?: string;
    schemaId?: string;
    schemaName?: string;
    fieldCount?: number;
    version?: number;
    verified?: boolean;
    appliedChanges?: string[];
    unappliedChanges?: string[];
}

type Phase = "checking" | "ready" | "denied" | "fetch-error" | "applying" | "applied" | "apply-error";

function findFieldByName(schema: FetchedSchema | null, name: string): FetchedField | undefined {
    return schema?.fields?.find((f) => f.name?.toLowerCase() === name.toLowerCase());
}

export default function UpdateSchemaTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<UpdateSchemaInput, UpdateSchemaOutput>) {
    const { dispatch, addToolOutput, businessAreaId, tenantId, schemaList, updateSchemaModelResult } = helpers;

    const [phase, setPhase] = useState<Phase>("checking");
    const [denyReason, setDenyReason] = useState<string | null>(null);
    const [fetchedSchema, setFetchedSchema] = useState<FetchedSchema | null>(null);
    const [unresolvedFieldNames, setUnresolvedFieldNames] = useState<string[]>([]);
    const initialisedRef = useRef(false);
    const resolvedRef = useRef(false);
    const applyRequestedRef = useRef(false);
    // Snapshot of the persistent updateSchemaModelResult slice taken at dispatch
    // time, so the watcher effect ignores a stale `{ success: true }` from a
    // previous update and only fires when a fresh result arrives.
    const resultAtDispatchRef = useRef<unknown>(null);

    // Resolve immediately: check redux list, then fetch full schema.
    useEffect(() => {
        if (initialisedRef.current) return;
        if (state !== "input-available") return;
        if (!input?.schemaId) return;
        initialisedRef.current = true;

        const inList = schemaList.some((s) => s.schemaId === input.schemaId);
        if (!inList) {
            setPhase("denied");
            setDenyReason(`"${input.schemaName}" is not in your loaded schema list. Open the schema list and try again.`);
            return;
        }
        if (!tenantId || !businessAreaId) {
            setPhase("denied");
            setDenyReason("Tenant or business area context not loaded.");
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const schema = await fetchSchemaById(tenantId, businessAreaId, input.schemaId);
                if (cancelled) return;
                if (!schema) {
                    setPhase("denied");
                    setDenyReason("Schema not found on the server.");
                    return;
                }
                setFetchedSchema(schema);
                const missing: string[] = [];
                (input.updateFields || []).forEach((u) => {
                    if (!findFieldByName(schema, u.fieldName)) missing.push(u.fieldName);
                });
                (input.deleteFields || []).forEach((d) => {
                    if (!findFieldByName(schema, d.fieldName)) missing.push(d.fieldName);
                });
                setUnresolvedFieldNames(missing);
                setPhase("ready");
            } catch (e: any) {
                if (cancelled) return;
                setPhase("fetch-error");
                setDenyReason(e?.message || "Failed to fetch schema details.");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [state, input, schemaList, tenantId, businessAreaId]);

    // Auto-resolve denial as a tool error so the LLM stops.
    useEffect(() => {
        if (resolvedRef.current) return;
        if (state === "output-available" || state === "output-error") return;
        if (phase === "denied" || phase === "fetch-error") {
            resolvedRef.current = true;
            addToolOutput({
                tool: "updateSchema",
                toolCallId,
                output: { success: false, error: denyReason || "Cannot update schema." },
            });
        }
    }, [phase, denyReason, state, toolCallId, addToolOutput]);

    // Watch redux update result after Apply.
    useEffect(() => {
        if (phase !== "applying") return;
        if (!updateSchemaModelResult) return;
        // Ignore the pre-dispatch result; wait for the fresh one.
        if (updateSchemaModelResult === resultAtDispatchRef.current) return;
        if (updateSchemaModelResult.success === true) {
            (async () => {
                const before = fetchedSchema;
                let refreshed: FetchedSchema | null = fetchedSchema;
                let fetchErr: string | null = null;
                try {
                    if (tenantId && businessAreaId && input?.schemaId) {
                        refreshed = await fetchSchemaById(tenantId, businessAreaId, input.schemaId);
                    }
                } catch (e: any) {
                    fetchErr = e?.message || "fetch error";
                }
                if (refreshed) setFetchedSchema(refreshed);
                setPhase("applied");
                if (!resolvedRef.current) {
                    resolvedRef.current = true;
                    let verified = true;
                    let appliedChanges: string[] = [];
                    let unappliedChanges: string[] = [];
                    if (!before) {
                        verified = false;
                        unappliedChanges = ["could not verify — original schema state was unavailable"];
                    } else if (fetchErr || !refreshed) {
                        verified = false;
                        unappliedChanges = [`could not re-fetch schema to verify (${fetchErr || "no response"})`];
                    } else {
                        const report = verifyUpdatedSchema(
                            before,
                            {
                                name: input?.name,
                                description: input?.description,
                                addFields: Array.isArray(input?.addFields) ? input!.addFields! : [],
                                updateFields: Array.isArray(input?.updateFields) ? input!.updateFields! : [],
                                deleteFields: Array.isArray(input?.deleteFields) ? input!.deleteFields! : [],
                            },
                            refreshed,
                        );
                        verified = report.verified;
                        appliedChanges = report.appliedChanges;
                        unappliedChanges = report.unappliedChanges;
                    }
                    addToolOutput({
                        tool: "updateSchema",
                        toolCallId,
                        output: {
                            success: true,
                            schemaId: refreshed?.schemaId || input?.schemaId,
                            schemaName: refreshed?.name || input?.schemaName,
                            fieldCount: Array.isArray(refreshed?.fields) ? refreshed!.fields!.length : undefined,
                            version: refreshed?.version,
                            verified,
                            appliedChanges,
                            unappliedChanges,
                        },
                    });
                }
            })();
        } else if (updateSchemaModelResult.success === false) {
            setPhase("apply-error");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({
                    tool: "updateSchema",
                    toolCallId,
                    output: buildToolError(updateSchemaModelResult, "Update failed."),
                });
            }
        }
    }, [updateSchemaModelResult, phase, fetchedSchema, tenantId, businessAreaId, input, toolCallId, addToolOutput]);

    const addFields = Array.isArray(input?.addFields) ? input!.addFields! : [];
    const updateFields = Array.isArray(input?.updateFields) ? input!.updateFields! : [];
    const deleteFieldsInput = Array.isArray(input?.deleteFields) ? input!.deleteFields! : [];
    const hasNameChange = !!input?.name && input.name !== fetchedSchema?.name;
    const hasDescChange = typeof input?.description === "string" && input.description !== fetchedSchema?.description;
    const hasFieldChange = addFields.length > 0 || updateFields.length > 0 || deleteFieldsInput.length > 0;
    const nothingToDo = !hasNameChange && !hasDescChange && !hasFieldChange;

    const handleApply = () => {
        if (!fetchedSchema || !businessAreaId || !input?.schemaId) return;
        if (applyRequestedRef.current) return;
        if (unresolvedFieldNames.length > 0) {
            applyRequestedRef.current = true;
            resolvedRef.current = true;
            addToolOutput({
                tool: "updateSchema",
                toolCallId,
                output: { success: false, error: `Field(s) not found: ${unresolvedFieldNames.join(", ")}` },
            });
            return;
        }

        let schemaModelChange: Record<string, unknown> | null;
        try {
            schemaModelChange = buildSchemaUpdateRequest(fetchedSchema, { name: input.name, description: input.description });
        } catch (e: any) {
            setPhase("apply-error");
            applyRequestedRef.current = true;
            resolvedRef.current = true;
            addToolOutput({ tool: "updateSchema", toolCallId, output: { success: false, error: e?.message || "Failed to build payload." } });
            return;
        }

        let updatedFieldsPayload: Record<string, unknown>[] = [];
        let deletedFieldsPayload: Record<string, unknown>[] = [];
        try {
            updatedFieldsPayload = updateFields.map((u) => {
                const existing = findFieldByName(fetchedSchema, u.fieldName)!;
                return buildUpdateFieldPayload(existing, u);
            });
            deletedFieldsPayload = deleteFieldsInput.map((d) => buildDeleteFieldPayload(findFieldByName(fetchedSchema, d.fieldName)!));
        } catch (e: any) {
            setPhase("apply-error");
            applyRequestedRef.current = true;
            resolvedRef.current = true;
            addToolOutput({ tool: "updateSchema", toolCallId, output: { success: false, error: e?.message || "Failed to build payload." } });
            return;
        }

        const newFieldsPayload = addFields.map(buildFieldPayload);

        const updateSchemaModelData = {
            schemaModel: schemaModelChange,
            newFields: newFieldsPayload,
            deletedFields: deletedFieldsPayload,
            updatedFields: updatedFieldsPayload,
        };
        const fieldIndexMappings = {
            newFieldIndexMappings: newFieldsPayload.reduce<Record<number, number>>((acc, _, i) => {
                acc[i] = i;
                return acc;
            }, {}),
            updatedSchemaFieldIndexMapping: updatedFieldsPayload.reduce<Record<number, number>>((acc, _, i) => {
                acc[i] = i;
                return acc;
            }, {}),
        };

        applyRequestedRef.current = true;
        resultAtDispatchRef.current = updateSchemaModelResult;
        setPhase("applying");
        dispatch(updateSchemaModelRequest(businessAreaId, input.schemaId, updateSchemaModelData, fieldIndexMappings, "assistant"));
    };

    const handleCancel = () => {
        if (!resolvedRef.current) {
            resolvedRef.current = true;
            addToolOutput({ tool: "updateSchema", toolCallId, output: { success: false, cancelled: true } });
        }
    };

    return (
        <div className="rounded-2xl border border-[#001D3D]/40 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-[#001D3D]">
                <EditOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Proposed update</span>
            </div>
            <div className="text-base font-semibold text-zinc-900">{fetchedSchema?.name || input?.schemaName || input?.schemaId}</div>
            <div className="text-[10px] text-zinc-500">
                id: {input?.schemaId}
                {fetchedSchema?.version !== undefined && ` · v${fetchedSchema.version}`}
            </div>

            {phase === "checking" && (
                <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500">
                    <LoadingOutlined spin /> Loading current schema...
                </div>
            )}

            {(phase === "denied" || phase === "fetch-error") && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {denyReason}
                </div>
            )}

            {(phase === "ready" || phase === "applying" || phase === "applied" || phase === "apply-error") && fetchedSchema && (
                <div className="mt-2 space-y-2 text-xs">
                    {hasNameChange && (
                        <div>
                            <span className="text-zinc-500">Rename to:</span> <span className="text-zinc-900">{input?.name}</span>
                        </div>
                    )}
                    {hasDescChange && (
                        <div>
                            <span className="text-zinc-500">New description:</span> <span className="text-zinc-900">{input?.description}</span>
                        </div>
                    )}
                    {addFields.length > 0 && (
                        <div>
                            <div className="text-zinc-500">Add fields:</div>
                            <ul className="ml-3 list-disc text-zinc-800">
                                {addFields.map((f, i) => (
                                    <li key={`add-${i}`}>
                                        <span className="font-medium">{f.name}</span> <span className="text-zinc-500">({f.dataType})</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {updateFields.length > 0 && (
                        <div>
                            <div className="text-zinc-500">Modify fields:</div>
                            <ul className="ml-3 list-disc text-zinc-800">
                                {updateFields.map((f, i) => {
                                    const found = findFieldByName(fetchedSchema, f.fieldName);
                                    const changes: string[] = [];
                                    if (f.newName) changes.push(`rename → ${f.newName}`);
                                    if (f.dataType !== undefined) changes.push(`type → ${f.dataType}`);
                                    if (f.description !== undefined) changes.push("description");
                                    if (f.isPrimaryKey !== undefined) changes.push(`PK=${f.isPrimaryKey}`);
                                    if (f.isSortKey !== undefined) changes.push(`SK=${f.isSortKey}`);
                                    if (f.isRequired !== undefined) changes.push(`required=${f.isRequired}`);
                                    if (f.isPII !== undefined) changes.push(`PII=${f.isPII}`);
                                    return (
                                        <li key={`upd-${i}`}>
                                            <span className="font-medium">{f.fieldName}</span>{" "}
                                            <span className="text-zinc-500">{found ? `(${changes.join(", ") || "no change"})` : "(not found)"}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                    {deleteFieldsInput.length > 0 && (
                        <div>
                            <div className="text-zinc-500">Delete fields:</div>
                            <ul className="ml-3 list-disc text-red-700">
                                {deleteFieldsInput.map((f, i) => (
                                    <li key={`del-${i}`}>
                                        <span className="font-medium">{f.fieldName}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {unresolvedFieldNames.length > 0 && (
                        <div className="rounded-md border border-yellow-200 bg-yellow-50 px-2 py-1 text-yellow-800">
                            Field(s) not found in this schema: {unresolvedFieldNames.join(", ")}
                        </div>
                    )}
                    {nothingToDo && <div className="text-zinc-500">No effective changes to apply.</div>}
                </div>
            )}

            {phase === "applied" && output?.verified !== false && (output?.unappliedChanges?.length || 0) === 0 && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">
                    <CheckCircleOutlined /> Updated.
                </div>
            )}
            {phase === "applied" && (output?.verified === false || (output?.unappliedChanges?.length || 0) > 0) && (
                <div className="mt-3 rounded-md border border-yellow-300 bg-yellow-50 px-2 py-1 text-xs text-yellow-800">
                    <div className="flex items-center gap-2 font-medium">
                        <WarningOutlined /> Some changes did not apply
                    </div>
                    <ul className="ml-4 mt-1 list-disc">
                        {(output?.unappliedChanges || []).map((c, i) => (
                            <li key={i}>{c}</li>
                        ))}
                    </ul>
                </div>
            )}
            {phase === "apply-error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> Update failed.
                </div>
            )}
            {state === "output-available" && output?.cancelled && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-600">
                    <CloseCircleOutlined /> Cancelled.
                </div>
            )}
            {state === "output-error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}

            {phase === "ready" && state !== "output-available" && state !== "output-error" && (
                <div className="mt-3 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="rounded-md border border-zinc-300 bg-white px-3 py-1 text-xs text-zinc-700 hover:bg-zinc-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={nothingToDo || unresolvedFieldNames.length > 0}
                        className="rounded-md bg-[#001D3D] px-3 py-1 text-xs font-medium text-white hover:bg-[#002D5C] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Apply
                    </button>
                </div>
            )}
            {phase === "applying" && (
                <div className="mt-3 flex items-center justify-end gap-2 text-xs text-zinc-500">
                    <LoadingOutlined spin /> Applying...
                </div>
            )}
        </div>
    );
}
